import { KeyboardEvent, SetStateAction, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react'

import { Alert, Box, CircularProgress, LinearProgress, Stack, TextField, Tooltip } from '@mui/material'
import { keyframes, styled } from '@mui/material/styles'
import {
    type BreadcrumbItem,
    CircleDashed,
    copyTextToClipboard,
    EmptyThreadsImage,
    FileDropArea,
    FileUploadCard,
    FormMarkdownField,
    InfoBox,
    InfoTooltip,
    Modal,
    TinyButton,
    tokens,
    TOOLBAR_HEIGHT,
} from 'ComponentLibrary'
import {
    AnchorSimpleIcon,
    ArrowClockwiseIcon,
    ArrowRightIcon,
    ArrowsClockwiseIcon,
    CaretDownIcon,
    CaretUpIcon,
    ChatCircleDotsIcon,
    CheckCircleIcon,
    CheckIcon,
    ClockCounterClockwiseIcon,
    CopyIcon,
    CursorClickIcon,
    FastForwardIcon,
    PencilSimpleIcon,
    QuestionIcon,
    ShareFatIcon,
    SparkleIcon,
    TrashIcon,
    UploadSimpleIcon,
    WarningIcon,
    XCircleIcon,
    XIcon,
} from '@phosphor-icons/react'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import {
    DEFAULT_PDF_PREVIEW_COPY,
    PdfPreviewSection,
    type PdfPreviewSectionCopy,
} from 'ComponentLibrary/src/playground/shared/pdf/PdfPreviewSection'
import { EmptyPdfStateCard, EmptyPdfStateContent, EmptyPdfStateIllustration } from 'ComponentLibrary/src/playground/shared/pdf/styled'
import { preprocessPdf } from 'ComponentLibrary/src/playground/shared/pdf/pdf-preprocessor'
import { loadSamplePdfFile } from 'ComponentLibrary/src/playground/shared/sample-pdf'
import { FeedbackTool, type FeedbackToolProps } from 'ComponentLibrary/src/molecules/FeedbackTool/FeedbackTool'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import { TextButtonNew } from 'ComponentLibrary/src/piazza/buttons'
import {
    FOLLOW_UP_QUESTIONS_STORAGE_KEY,
    PDF_STAGE_HEIGHT_PX,
    PDF_STAGE_WIDTH_PX,
    QUESTIONS_STORAGE_KEY,
} from 'ComponentLibrary/src/playground/tools/PdfValidator/constants'
import { createQuestionId, loadPersistedQuestions } from 'ComponentLibrary/src/playground/tools/PdfValidator/utils/pdf-validator-helpers'
import {
    applyPendingQuestionEdit,
    createInitialPdfValidatorState,
    pdfValidatorReducer,
    PdfValidatorState,
} from 'ComponentLibrary/src/playground/tools/PdfValidator/pdf-validator-state'
import {
    FocusHighlight,
    TableOfContentsEntry,
    ValidationQuestion,
    ValidationQuestionDefinition,
    ValidationResult,
} from 'ComponentLibrary/src/playground/tools/PdfValidator/types'
import { useForm } from 'react-hook-form'
import { hasFileAboveMaxSize, MAX_PLAYGROUND_FILE_SIZE_BYTES } from 'ComponentLibrary/src/playground/shared/upload/file-size'
import { readFileAsBase64 } from 'ComponentLibrary/src/playground/shared/upload/read-file'
import {
    DEFAULT_PLAYGROUND_TOOL_HEADER_COPY,
    PlaygroundToolHeader,
    type PlaygroundToolHeaderCopy,
} from 'ComponentLibrary/src/playground/shared/layout/PlaygroundToolHeader'
import { PlaygroundToolPageContainer as RootContainer } from 'ComponentLibrary/src/playground/shared/layout/PlaygroundToolPageContainer'
import { DEFAULT_FILE_UPLOAD_COPY, type FileUploadCopy } from 'ComponentLibrary/src/playground/shared/upload/file-upload-copy'
import type { FormMarkdownFieldCopy } from 'ComponentLibrary/src/atoms/forms/FormTextFields/FormTextMarkdownField/FormMarkdownField'
import {
    buildPlausibilityAnalyzeCommand,
    buildPlausibilityAnswerCommand,
    toValidationResultItems,
    type PdfValidatorAnalyzeWorkflowResult,
    type PdfValidatorAnswerWorkflowResult,
    type PdfValidatorWorkflowInvoker,
    type PdfValidatorWorkflowPdfFile,
    type PdfValidatorWorkflowProgressSnapshot,
    type PdfValidatorWorkflowResultItem,
    type PdfValidatorWorkflowWarning,
} from 'ComponentLibrary/src/playground/tools/PdfValidator/utils/workflow'

export type {
    PdfValidatorWorkflowInvoker,
    PdfValidatorWorkflowProgressSnapshot,
    PdfValidatorWorkflowWarning,
} from 'ComponentLibrary/src/playground/tools/PdfValidator/utils/workflow'

const StyledMarkdownAnswer = styled(Box)(() => ({
    overflowWrap: 'break-word',
    fontSize: tokens.FontSizeSm,
    color: tokens.ColorTextDefault,
    '& *': {
        marginTop: 0,
    },
    '& p:last-child': {
        marginBottom: 0,
    },
    '& ul, & ol': {
        margin: 0,
        paddingLeft: tokens.SizingSpacing200,
    },
    '& li + li': {
        marginTop: tokens.SizingSpacing050,
    },
    '& code': {
        fontFamily: 'monospace',
    },
}))

const loadingEllipsis = keyframes({
    '0%, 20%': {
        opacity: 0.2,
    },
    '50%': {
        opacity: 1,
    },
    '100%': {
        opacity: 0.2,
    },
})

const StyledLoadingEllipsis = styled('span')(() => ({
    display: 'inline-flex',
    alignItems: 'baseline',
    width: '0.9em',
    minWidth: '0.9em',
    marginLeft: '2px',
    verticalAlign: 'baseline',
    lineHeight: 'inherit',
    transform: 'translateY(-0.04em)',
    justifyContent: 'space-between',
    '& > span': {
        display: 'inline-block',
        lineHeight: 'inherit',
        minWidth: '0.2em',
        textAlign: 'center',
        animation: `${loadingEllipsis} 1.2s ease-in-out infinite`,
    },
    '& > span:nth-of-type(1)': {
        marginRight: 0,
    },
    '& > span:nth-of-type(2)': {
        marginRight: 0,
        animationDelay: '0.15s',
    },
    '& > span:nth-of-type(3)': {
        animationDelay: '0.3s',
    },
}))

const LoadingEllipsis = () => (
    <StyledLoadingEllipsis aria-hidden>
        <span>.</span>
        <span>.</span>
        <span>.</span>
    </StyledLoadingEllipsis>
)

const getPdfValidatorInfoModalIcon = (index: number) => {
    switch (index) {
        case 0:
            return <SparkleIcon />
        case 1:
            return <ClockCounterClockwiseIcon />
        case 2:
            return <CursorClickIcon />
        case 3:
            return <AnchorSimpleIcon />
        case 4:
            return <ShareFatIcon />
        default:
            return <SparkleIcon />
    }
}

const getAnswerLabel = (status: ValidationResult['status'], copy: PdfValidatorCopy): string => {
    if (status === 'yes') {
        return copy.answerLabels.yes
    }
    if (status === 'no') {
        return copy.answerLabels.no
    }
    if (status === 'text_answer') {
        return copy.answerLabels.textAnswer
    }
    return copy.answerLabels.unknown
}

class WorkflowCancelledError extends Error {}

export type PdfValidatorCopy = {
    locale: 'de' | 'en'
    toolHeader: PlaygroundToolHeaderCopy
    pdfPreview: PdfPreviewSectionCopy
    upload: FileUploadCopy
    title: string
    infoText: string
    infoModalItems: string[]
    emptyDescription: string
    answerLabels: {
        yes: string
        no: string
        textAnswer: string
        unknown: string
    }
    errors: {
        noClearAnswer: string
        retryValidation: string
        noReadableText: string
        tableOfContentsLoadingFailed: string
        pdfProcessing: string
        pdfProviderFit: string
        samplePdf: string
    }
    progress: {
        tableOfContents: string
    }
    standardChecks: {
        title: string
        tooltip: string
        questions: ValidationQuestionDefinition[]
    }
    tableOfContents: {
        title: string
        loading: string
        empty: string
        pending: string
    }
    notes: {
        title: string
        copy: string
        placeholder: string
        textEditor: FormMarkdownFieldCopy
    }
    questions: {
        title: string
        queued: string
        start: string
        startInfo: string
        newPlaceholder: string
        create: string
        emptyWithPersisted: string
        emptyWithoutPersisted: string
        generateExamples: string
        loadPast: string
        save: string
        cancelEdit: string
        edit: string
        delete: string
        examples: string[]
    }
}

export const DEFAULT_PDF_VALIDATOR_COPY: PdfValidatorCopy = {
    locale: 'de',
    toolHeader: DEFAULT_PLAYGROUND_TOOL_HEADER_COPY,
    pdfPreview: DEFAULT_PDF_PREVIEW_COPY,
    upload: {
        ...DEFAULT_FILE_UPLOAD_COPY,
        sampleTooltip: 'Mit einwert Beispiel Gutachten testen',
    },
    title: 'Auto-Plausibilisierung',
    infoText: 'Prüfen Sie mit der Auto-Plausibilisierung KI-unterstützt Ihre Gutachten.',
    infoModalItems: [
        'Durch eine Kombination aus vordefinierten Standard-Checks und Ihren eigenen Prüffragen erhalten Sie schnell Antworten zu dem vorliegenden Gutachten.',
        'Sie können die Prüffragen vom letzten Durchlauf laden und so schnell dieselben Checks auf einem neuen Gutachten wiederholen.',
        'Die Standard-Checks werden sofort automatisch ausgeführt. Sie können Ihre eigenen Prüffragen hinzufügen und diese dann mit Klick auf "Plausibilisierung starten" einreihen.',
        'Der Klick auf eine Antwort führt zu der relevanten Passage des Gutachtens und dient zur Kontrolle der KI-Resultate.',
        'Sie können Ihre eigenen Anmerkungen im Notizfeld hinterlegen und anschließend mit Anderen teilen.',
    ],
    emptyDescription: 'Wählen Sie ein Gutachten aus, um mit der Plausibilisierung zu starten.',
    answerLabels: {
        yes: 'Ja',
        no: 'Nein',
        textAnswer: 'Textantwort',
        unknown: 'Unklar',
    },
    errors: {
        noClearAnswer: 'Keine eindeutige Antwort im Dokument gefunden.',
        retryValidation:
            'Ein Teil der Plausibilisierung ist fehlgeschlagen. Bitte klicken Sie erneut auf "Plausibilisierung starten", damit alle offenen Checks und Prüffragen beantwortet werden.',
        noReadableText: 'Im Dokument wurden keine lesbaren Texte gefunden.',
        tableOfContentsLoadingFailed:
            'Das Inhaltsverzeichnis konnte nicht geladen werden. Die Plausibilisierung kann trotzdem gestartet werden.',
        pdfProcessing: 'Die PDF-Datei konnte nicht verarbeitet werden. Prüfe bitte das Dokument und versuche es erneut.',
        pdfProviderFit:
            'Die PDF-Datei konnte nicht für die KI-Verarbeitung vorbereitet werden. Bitte komprimieren Sie die Datei und versuchen Sie es erneut.',
        samplePdf: 'Das Mustergutachten konnte nicht geladen werden.',
    },
    progress: {
        tableOfContents: 'Inhaltsverzeichnis wird geladen',
    },
    standardChecks: {
        title: 'Standard Checks',
        tooltip: 'Diese Beispiel Standard-Checks sind aktuell auf Folgebewertungen ausgerichtet. Weitere Bewertungsarten folgen.',
        questions: [
            {
                id: 'standard_check_aktuelle_quellen',
                question: 'Die Marktdaten Quellen sind nicht älter als zwei Jahre vor Wertermittungsstichtag',
            },
            {
                id: 'standard_check_grundbuchauszug',
                question: 'Der Grundbuchauszug ist maximal drei Jahre alt',
            },
            {
                id: 'standard_check_wirtschaftliches_baujahr',
                question: 'Das wirtschaftliche Baujahr hat sich nicht verändert',
            },
            {
                id: 'standard_check_besichtigung_hauptgutachter',
                question: 'Die Besichtigung wurde u.a. durch den Hauptgutachter durchgeführt',
            },
        ],
    },
    tableOfContents: {
        title: 'Inhaltsverzeichnis',
        loading: 'Inhaltsverzeichnis wird geladen',
        empty: 'Keine Hauptabschnitte erkannt.',
        pending: 'Das Inhaltsverzeichnis wird nach dem Upload automatisch erstellt.',
    },
    notes: {
        title: 'Notizen',
        copy: 'Notizen kopieren',
        placeholder: 'Sammeln Sie hier Ihre Anmerkungen',
        textEditor: {
            toolbar: {
                textStyle: 'Textstil',
                bold: 'Fett',
                italic: 'Kursiv',
                bulletedList: 'Aufzählung',
                numberedList: 'Nummerierte Liste',
                headingOptions: {
                    paragraph: 'Text',
                    h2: 'Headline 1',
                    h3: 'Headline 2',
                    h4: 'Headline 3',
                    h5: 'Headline 4',
                },
            },
        },
    },
    questions: {
        title: 'Prüffragen',
        queued: 'Prüffragen eingereiht',
        start: 'Plausibilisierung starten',
        startInfo: 'Plausibilisierung starten um unbeantwortete Fragen zu beantworten.',
        newPlaceholder: 'Neue Frage ...',
        create: 'Prüffrage erstellen',
        emptyWithPersisted:
            'Fügen Sie eigene Prüffragen hinzu, starten Sie mit Beispielfragen oder laden Sie die Fragen der letzten Plausibilisierung.',
        emptyWithoutPersisted: 'Fügen Sie eigene Prüffragen hinzu oder starten Sie mit Beispielfragen.',
        generateExamples: 'Beispielfragen generieren',
        loadPast: 'Vergangene Fragen laden',
        save: 'Prüffrage speichern',
        cancelEdit: 'Bearbeitung abbrechen',
        edit: 'Prüffrage bearbeiten',
        delete: 'Prüffrage löschen',
        examples: [
            'Woraus resultieren die wesentlichen Änderungen zur Vorbewertung?',
            'Wurden die CapEx-Maßnahmen so beschrieben, dass sie für einen unabhängigen Dritten nachvollziehbar sind?',
            'Wurde die Veränderung des LZ so beschrieben, dass sie für einen unabhängigen Dritten nachvollziehbar ist?',
            'Es gibt keine logische Widersprüche im Gutachten. Falls doch, liste alle.',
            'Es gibt keine Rechtschreibfehler im Dokument. Falls doch, liste alle.',
            'Es gibt keine verbleibende Platzhalter im Dokument. Falls doch, liste alle.',
            'Die Gegenüberstellung zur Vorbewertung rechnet die korrekte absolute und prozentuale Veränderung.',
        ],
    },
}

type PdfValidatorProps = {
    invokeWorkflow: PdfValidatorWorkflowInvoker
    feedbackButton?: FeedbackToolProps
    breadcrumbRoot?: BreadcrumbItem
    onDataPrivacyModalOpen?: () => void
    onInfoModalOpen?: () => void
    onSamplePdfUsed?: () => void
    copy?: PdfValidatorCopy
}

const buildWorkflowPdf = async (file: File): Promise<PdfValidatorWorkflowPdfFile> => ({
    name: file.name,
    source: {
        sourceType: 'BYTE_CONTENT',
        byteContent: {
            mediaType: 'application/pdf',
            data: await readFileAsBase64(file),
        },
    },
})

const toWorkflowErrorMessage = (error: unknown, copy: PdfValidatorCopy['errors'], fallback: string) =>
    error instanceof Error && error.message === 'pdf_provider_fit_failed' ? copy.pdfProviderFit : fallback

const getLocalizedText = (text: { de: string; en: string }, locale: PdfValidatorCopy['locale']) => text[locale]

const normalizeQuestionText = (question: string) => question.trim().replace(/\s+/g, ' ').toLowerCase()
const statusIconStyle = { flexShrink: 0 } as const

const renderStandardCheckIcon = ({
    result,
    isPending,
    isActivePending,
}: Pick<ValidationQuestion, 'result' | 'isPending'> & { isActivePending: boolean }) => {
    if (isPending && isActivePending) {
        return (
            <CircularProgress
                size={tokens.SizingIconS}
                thickness={4}
                style={{ position: 'relative', top: 0, left: 0, color: tokens.ColorIconBoldest, flexShrink: 0 }}
            />
        )
    }

    if (!result) {
        return (
            <CircleDashed
                width={tokens.SizingIconS}
                height={tokens.SizingIconS}
                style={{ color: tokens.ColorIconDisabled, flexShrink: 0 }}
            />
        )
    }

    if (result.status === 'unknown') {
        return <QuestionIcon size={tokens.SizingIconS} color={tokens.ColorIconDisabled} weight="fill" style={statusIconStyle} />
    }

    if (result.status === 'yes') {
        return <CheckCircleIcon size={tokens.SizingIconS} color={tokens.ColorIconPositive} weight="fill" style={statusIconStyle} />
    }

    if (result.status === 'text_answer') {
        return <ChatCircleDotsIcon size={tokens.SizingIconS} color={tokens.ColorIconInformative} weight="fill" style={statusIconStyle} />
    }

    return <XCircleIcon size={tokens.SizingIconS} color={tokens.ColorIconDanger} weight="fill" style={statusIconStyle} />
}

const getValidationAnswerBodyText = (result: ValidationResult | null, copy: PdfValidatorCopy): string | null => {
    if (!result) {
        return null
    }

    const answerText = result.answer?.trim()
    if (answerText) {
        return answerText
    }

    const sourceText = result.sourceText?.trim()
    if (sourceText) {
        return sourceText
    }

    if (result.status === 'unknown') {
        return copy.errors.noClearAnswer
    }

    return getAnswerLabel(result.status, copy)
}

const formatStandardCheckTooltip = (result: ValidationResult | null, copy: PdfValidatorCopy): string | null => {
    if (!result) {
        return null
    }

    const answer = getValidationAnswerBodyText(result, copy)
    const page = result.page ? ` (${copy.pdfPreview.page} ${result.page})` : ''
    return answer ? `${answer}${page}` : page || null
}

const TableOfContentsLoadingState = ({ copy }: { copy: PdfValidatorCopy }) => (
    <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
        <CircularProgress
            size={tokens.SizingIconS}
            thickness={4}
            style={{ position: 'relative', top: 0, left: 0, color: tokens.ColorIconBoldest, flexShrink: 0 }}
        />
        <Typography variant="textS" color={tokens.ColorTextBold}>
            {copy.tableOfContents.loading}
            <LoadingEllipsis />
        </Typography>
    </Stack>
)

const getExpandableAnswerSurfaceColor = (status: ValidationResult['status'] | null) => {
    if (status === 'yes') {
        return tokens.ColorSurfacePositiveDefault
    }

    if (status === 'no') {
        return tokens.ColorSurfaceDangerDefault
    }

    if (status === 'text_answer') {
        return tokens.ColorSurfaceInformativeDefault
    }

    return tokens.ColorSurfaceAccentGreyDefault
}

const getValidationAnswerTextColor = (_status: ValidationResult['status'] | null) => tokens.ColorTextDefault

const getValidationAnswerPreviewText = (result: ValidationResult | null, copy: PdfValidatorCopy) => {
    return getValidationAnswerBodyText(result, copy)
}

const renderValidationQuestionStatusIcon = ({
    result,
    isPending,
    isActivePending,
}: {
    result: ValidationResult | null
    isPending: boolean
    isActivePending: boolean
}) => {
    if (isPending && isActivePending) {
        return (
            <CircularProgress
                size={tokens.SizingIconS}
                thickness={4}
                style={{ position: 'relative', top: 0, left: 0, color: tokens.ColorIconBoldest, flexShrink: 0 }}
            />
        )
    }

    if (!result) {
        return (
            <CircleDashed
                width={tokens.SizingIconS}
                height={tokens.SizingIconS}
                style={{ color: tokens.ColorIconDisabled, flexShrink: 0 }}
            />
        )
    }

    if (result.status === 'unknown') {
        return <QuestionIcon size={tokens.SizingIconS} color={tokens.ColorIconDisabled} weight="fill" style={statusIconStyle} />
    }

    if (result.status === 'yes') {
        return <CheckCircleIcon size={tokens.SizingIconS} color={tokens.ColorIconPositive} weight="fill" style={statusIconStyle} />
    }

    if (result.status === 'text_answer') {
        return <ChatCircleDotsIcon size={tokens.SizingIconS} color={tokens.ColorIconInformative} weight="fill" style={statusIconStyle} />
    }

    return <XCircleIcon size={tokens.SizingIconS} color={tokens.ColorIconDanger} weight="fill" style={statusIconStyle} />
}

const PANEL_PAIR_GAP_PX = 16
const PANEL_PAIR_HEIGHT_ADJUSTMENT_PX = 92
const PDF_STAGE_WIDTH = `${PDF_STAGE_WIDTH_PX}px`
const PDF_STAGE_HEIGHT = `${PDF_STAGE_HEIGHT_PX}px`
const PANEL_COLUMN_MAX_HEIGHT = `${PDF_STAGE_HEIGHT_PX - PANEL_PAIR_HEIGHT_ADJUSTMENT_PX}px`
const NOTES_EDITOR_CHROME_HEIGHT_PX = 120
type PanelPairHeights = {
    top: number | undefined
    bottom: number | undefined
}

const measurePanelNaturalHeight = (element: HTMLElement | null): number => {
    if (!element) {
        return 0
    }

    const styles = window.getComputedStyle(element)
    const borderHeight = (parseFloat(styles.borderTopWidth || '0') || 0) + (parseFloat(styles.borderBottomWidth || '0') || 0)
    return Math.ceil(element.scrollHeight + borderHeight)
}

const resolvePanelPairHeights = (
    topElement: HTMLElement | null,
    bottomElement: HTMLElement | null,
    overflowTopRatio = 0.5
): PanelPairHeights => {
    if (!topElement || !bottomElement) {
        return { top: undefined, bottom: undefined }
    }

    const availableHeight = PDF_STAGE_HEIGHT_PX - PANEL_PAIR_GAP_PX - PANEL_PAIR_HEIGHT_ADJUSTMENT_PX
    const topOverflowHeight = Math.floor(availableHeight * overflowTopRatio)
    const bottomOverflowHeight = Math.max(0, availableHeight - topOverflowHeight)
    const topNaturalHeight = Math.max(0, measurePanelNaturalHeight(topElement))
    const bottomNaturalHeight = Math.max(0, measurePanelNaturalHeight(bottomElement))

    if (topNaturalHeight + bottomNaturalHeight <= availableHeight) {
        return {
            top: topNaturalHeight,
            bottom: bottomNaturalHeight,
        }
    }

    if (topNaturalHeight > topOverflowHeight && bottomNaturalHeight > bottomOverflowHeight) {
        return {
            top: topOverflowHeight,
            bottom: bottomOverflowHeight,
        }
    }

    if (topNaturalHeight <= topOverflowHeight) {
        return {
            top: topNaturalHeight,
            bottom: Math.max(0, availableHeight - topNaturalHeight),
        }
    }

    return {
        top: Math.max(0, availableHeight - bottomNaturalHeight),
        bottom: bottomNaturalHeight,
    }
}

const findNearestScrollableAncestor = (element: HTMLElement | null): HTMLElement | Window => {
    let current = element?.parentElement ?? null

    while (current) {
        const styles = window.getComputedStyle(current)
        const overflowY = styles.overflowY
        const isScrollable = ['auto', 'scroll', 'overlay'].includes(overflowY) && current.scrollHeight > current.clientHeight

        if (isScrollable) {
            return current
        }

        current = current.parentElement
    }

    return window
}

const PageLayoutGrid = styled(Box)(() => ({
    display: 'grid',
    gridTemplateColumns: `minmax(240px, 1fr) minmax(0, ${PDF_STAGE_WIDTH}) minmax(320px, 1.15fr)`,
    gap: tokens.SizingSpacing200,
    width: '100%',
    alignItems: 'start',
}))

const PanelColumn = styled(Stack)(() => ({
    gap: tokens.SizingSpacing200,
    minWidth: 0,
    maxHeight: PANEL_COLUMN_MAX_HEIGHT,
}))

const PanelCard = styled(Stack)(() => ({
    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    borderRadius: tokens.SizingCornerM,
    backgroundColor: tokens.ColorSurfaceDefault,
    padding: tokens.SizingSpacing200,
    minWidth: 0,
    boxSizing: 'border-box',
}))

const QuestionsPanel = styled(PanelCard)(() => ({
    padding: tokens.SizingSpacing200,
    overflowX: 'hidden',
    overflowY: 'auto',
}))

const PdfStage = styled(Box)(() => ({
    minWidth: 0,
    width: `calc(100% - ${tokens.SizingSpacing800})`,
    maxWidth: PDF_STAGE_WIDTH,
    maxHeight: PDF_STAGE_HEIGHT,
    position: 'sticky',
    top: tokens.SizingSpacing300,
    justifySelf: 'center',
}))

type NotesFormValues = {
    notes: string
}

export const PdfValidator = ({
    invokeWorkflow,
    breadcrumbRoot,
    feedbackButton,
    onDataPrivacyModalOpen,
    onInfoModalOpen,
    onSamplePdfUsed,
    copy = DEFAULT_PDF_VALIDATOR_COPY,
}: PdfValidatorProps) => {
    const [state, dispatch] = useReducer(pdfValidatorReducer, undefined, () =>
        createInitialPdfValidatorState(copy.standardChecks.questions)
    )
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
    const [modalSelectedFile, setModalSelectedFile] = useState<File | null>(null)
    const [isEmptyStateDropOver, setIsEmptyStateDropOver] = useState(false)
    const [pageJumpTarget, setPageJumpTarget] = useState<{ page: number; key: number; align?: 'exact' | 'offset' } | null>(null)
    const [leftPairHeights, setLeftPairHeights] = useState<PanelPairHeights>({ top: undefined, bottom: undefined })
    const [rightPairHeights, setRightPairHeights] = useState<PanelPairHeights>({ top: undefined, bottom: undefined })
    const [activeStandardCheckProcessingIds, setActiveStandardCheckProcessingIds] = useState<string[]>([])
    const [activeQuestionProcessingIds, setActiveQuestionProcessingIds] = useState<string[]>([])
    const [expandedQuestionAnswers, setExpandedQuestionAnswers] = useState<Record<string, boolean>>({})
    const [hoveredQuestionCardId, setHoveredQuestionCardId] = useState<string | null>(null)
    const [hoveredQuestionAnswerId, setHoveredQuestionAnswerId] = useState<string | null>(null)
    const [newQuestionValue, setNewQuestionValue] = useState('')
    const [persistedQuestions, setPersistedQuestions] = useState<ValidationQuestion[]>(() =>
        loadPersistedQuestions<ValidationResult>(QUESTIONS_STORAGE_KEY)
    )
    const [hasStartedPlausibility, setHasStartedPlausibility] = useState(false)
    const [failedValidationIds, setFailedValidationIds] = useState<string[]>([])
    const rootContainerRef = useRef<HTMLDivElement | null>(null)
    const leftTopPanelRef = useRef<HTMLDivElement | null>(null)
    const leftBottomPanelRef = useRef<HTMLDivElement | null>(null)
    const rightTopPanelRef = useRef<HTMLDivElement | null>(null)
    const rightBottomPanelRef = useRef<HTMLDivElement | null>(null)
    const pipelineVersionRef = useRef(0)
    const fileSelectionLockRef = useRef(false)
    const shouldPersistQuestionsRef = useRef(false)
    const {
        control: notesControl,
        watch: watchNotes,
        formState: { errors: notesErrors },
    } = useForm<NotesFormValues>({
        defaultValues: {
            notes: '',
        },
    })
    const {
        uploadedFile,
        pdfUrl,
        pageTexts,
        uploadError,
        checksError,
        questionsError,
        tableOfContentsError,
        isPreprocessing,
        isTableOfContentsLoading,
        isChecksRunning,
        isQuestionsRunning,
        focusHighlight,
        hasLoadedTableOfContents,
        tableOfContentsEntries,
        standardChecks,
        questions,
        editingQuestionId,
        editingQuestionValue,
    } = state
    const patchState = useCallback((payload: Partial<PdfValidatorState>) => {
        dispatch({ type: 'PATCH', payload })
    }, [])
    const setUploadedFile = useCallback((value: File | null) => patchState({ uploadedFile: value }), [patchState])
    const setPdfUrl = useCallback((value: string | null) => patchState({ pdfUrl: value }), [patchState])
    const setPageTexts = useCallback((value: string[]) => patchState({ pageTexts: value }), [patchState])
    const setUploadError = useCallback((value: string | null) => patchState({ uploadError: value }), [patchState])
    const setChecksError = useCallback((value: string | null) => patchState({ checksError: value }), [patchState])
    const setQuestionsError = useCallback((value: string | null) => patchState({ questionsError: value }), [patchState])
    const setTableOfContentsError = useCallback((value: string | null) => patchState({ tableOfContentsError: value }), [patchState])
    const setIsPreprocessing = useCallback((value: boolean) => patchState({ isPreprocessing: value }), [patchState])
    const setIsTableOfContentsLoading = useCallback((value: boolean) => patchState({ isTableOfContentsLoading: value }), [patchState])
    const setIsChecksRunning = useCallback((value: boolean) => patchState({ isChecksRunning: value }), [patchState])
    const setIsQuestionsRunning = useCallback((value: boolean) => patchState({ isQuestionsRunning: value }), [patchState])
    const setFocusHighlight = useCallback((value: FocusHighlight) => patchState({ focusHighlight: value }), [patchState])
    const setHasLoadedTableOfContents = useCallback((value: boolean) => patchState({ hasLoadedTableOfContents: value }), [patchState])
    const setTableOfContentsEntries = useCallback(
        (value: TableOfContentsEntry[]) => patchState({ tableOfContentsEntries: value }),
        [patchState]
    )
    const setStandardChecks = useCallback((value: SetStateAction<ValidationQuestion[]>) => {
        dispatch({ type: 'SET_STANDARD_CHECKS', payload: value })
    }, [])
    const setQuestions = useCallback((value: SetStateAction<ValidationQuestion[]>) => {
        shouldPersistQuestionsRef.current = true
        dispatch({ type: 'SET_QUESTIONS', payload: value })
    }, [])
    useEffect(() => {
        const definitionById = new Map(copy.standardChecks.questions.map((question) => [question.id, question.question]))
        setStandardChecks((previous) =>
            previous.map((question) => {
                const translatedQuestion = definitionById.get(question.id)
                return translatedQuestion ? { ...question, question: translatedQuestion } : question
            })
        )
    }, [copy.standardChecks.questions, setStandardChecks])
    const clearEditingQuestionState = useCallback(() => {
        dispatch({ type: 'CLEAR_EDITING_QUESTION_STATE' })
    }, [])
    const updateEditingQuestionValue = useCallback((value: string) => {
        dispatch({ type: 'UPDATE_EDITING_QUESTION_VALUE', payload: value })
    }, [])
    const startEditingQuestion = useCallback((id: string, value: string) => {
        dispatch({ type: 'START_EDITING_QUESTION', payload: { id, value } })
    }, [])
    const commitEditingQuestion = useCallback(() => {
        shouldPersistQuestionsRef.current = true
        dispatch({ type: 'COMMIT_EDITING_QUESTION' })
    }, [])
    const deleteQuestionById = useCallback((id: string) => {
        shouldPersistQuestionsRef.current = true
        dispatch({ type: 'DELETE_QUESTION', payload: { id } })
    }, [])
    const [workflowProgress, setWorkflowProgress] = useState<PdfValidatorWorkflowProgressSnapshot | null>(null)
    const [workflowWarnings, setWorkflowWarnings] = useState<PdfValidatorWorkflowWarning[]>([])
    const createNextPipelineVersion = useCallback(() => {
        pipelineVersionRef.current += 1
        return pipelineVersionRef.current
    }, [])
    const assertActivePipelineVersion = useCallback((pipelineVersion: number) => {
        if (pipelineVersionRef.current !== pipelineVersion) {
            throw new WorkflowCancelledError()
        }
    }, [])
    const toggleExpandedQuestionAnswer = useCallback((questionKey: string) => {
        setExpandedQuestionAnswers((previous) => ({
            ...previous,
            [questionKey]: !previous[questionKey],
        }))
    }, [])

    const isBusy = isPreprocessing || isTableOfContentsLoading || isChecksRunning || isQuestionsRunning
    const failedValidationIdSet = useMemo(() => new Set(failedValidationIds), [failedValidationIds])
    const primaryBorder = `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`
    const hasAnyQuestions = questions.some((question) => question.question.trim().length > 0)
    const hasPersistedQuestions = persistedQuestions.some((question) => question.question.trim().length > 0)
    const questionDefinitionsStorageValue = useMemo(
        () =>
            JSON.stringify(
                questions.map(({ id, question }) => ({
                    id,
                    question,
                }))
            ),
        [questions]
    )
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl)
            }
        }
    }, [pdfUrl])

    useEffect(() => {
        if (!shouldPersistQuestionsRef.current || typeof window === 'undefined') {
            return
        }

        try {
            window.localStorage.setItem(QUESTIONS_STORAGE_KEY, questionDefinitionsStorageValue)
            window.localStorage.removeItem(FOLLOW_UP_QUESTIONS_STORAGE_KEY)
            setPersistedQuestions(loadPersistedQuestions<ValidationResult>(QUESTIONS_STORAGE_KEY))
        } catch {
            // Ignore localStorage write failures (e.g., quota/private mode).
        }
    }, [questionDefinitionsStorageValue])
    const applyWorkflowResults = useCallback(
        (workflowResults: PdfValidatorWorkflowResultItem[]) => {
            const resultsById = new Map(toValidationResultItems(workflowResults).map(({ id, ...result }) => [id, result] as const))

            setStandardChecks((previous) =>
                previous.map((question) => {
                    const result = resultsById.get(question.id)
                    return result ? { ...question, result, isPending: false } : question
                })
            )
            setQuestions((previous) =>
                previous.map((question) => {
                    const result = resultsById.get(question.id)
                    return result ? { ...question, result, isPending: false } : question
                })
            )
        },
        [setQuestions, setStandardChecks]
    )

    const runAnalyzeWorkflow = useCallback(
        async ({
            file,
            questionDefinitions,
            pipelineVersion,
        }: {
            file: File
            questionDefinitions: ValidationQuestionDefinition[]
            pipelineVersion: number
        }) => {
            const standardCheckIds = new Set(copy.standardChecks.questions.map(({ id }) => id))
            const targetIds = new Set(questionDefinitions.map((question) => question.id))
            const standardTargetIds = new Set(questionDefinitions.filter(({ id }) => standardCheckIds.has(id)).map(({ id }) => id))
            const questionTargetIds = new Set(questionDefinitions.filter(({ id }) => !standardCheckIds.has(id)).map(({ id }) => id))
            const receivedResultIds = new Set<string>()
            let hasTableOfContents = false

            setWorkflowProgress(null)
            setWorkflowWarnings([])
            setTableOfContentsError(null)
            setChecksError(null)
            setQuestionsError(null)
            setFailedValidationIds((previous) => previous.filter((id) => !targetIds.has(id)))
            setIsTableOfContentsLoading(true)
            setIsChecksRunning(standardTargetIds.size > 0)
            setIsQuestionsRunning(questionTargetIds.size > 0)
            setActiveStandardCheckProcessingIds([...standardTargetIds])
            setActiveQuestionProcessingIds([...questionTargetIds])
            setStandardChecks((previous) =>
                previous.map((question) => (standardTargetIds.has(question.id) ? { ...question, result: null, isPending: true } : question))
            )
            setQuestions((previous) =>
                previous.map((question) => (questionTargetIds.has(question.id) ? { ...question, result: null, isPending: true } : question))
            )

            try {
                const response = await invokeWorkflow({
                    prompt: buildPlausibilityAnalyzeCommand(questionDefinitions),
                    files: [await buildWorkflowPdf(file)],
                    onProgress: (progress) => {
                        if (pipelineVersionRef.current !== pipelineVersion) {
                            return
                        }
                        setWorkflowProgress(progress)
                        setWorkflowWarnings(progress.warnings)
                    },
                    onPartialResult: (partialResult) => {
                        if (pipelineVersionRef.current !== pipelineVersion) {
                            return
                        }
                        if (partialResult.step_id === 'table_of_contents') {
                            hasTableOfContents = true
                            setTableOfContentsEntries(partialResult.value as TableOfContentsEntry[])
                            setHasLoadedTableOfContents(true)
                            setIsTableOfContentsLoading(false)
                            setTableOfContentsError(null)
                        }
                        if (partialResult.step_id === 'results') {
                            const partialResults = partialResult.value as PdfValidatorWorkflowResultItem[]
                            partialResults.forEach(({ id }) => receivedResultIds.add(id))
                            applyWorkflowResults(partialResults)
                        }
                    },
                })
                assertActivePipelineVersion(pipelineVersion)

                const result = JSON.parse(response.response) as PdfValidatorAnalyzeWorkflowResult
                hasTableOfContents = true
                setTableOfContentsEntries(result.table_of_contents)
                setHasLoadedTableOfContents(true)
                setTableOfContentsError(null)
                applyWorkflowResults(result.results)
                setWorkflowWarnings(result.warnings)
            } catch (error) {
                if (error instanceof WorkflowCancelledError) {
                    return
                }
                if (targetIds.size > 0 && [...targetIds].every((id) => receivedResultIds.has(id))) {
                    setFailedValidationIds((previous) => [...new Set([...previous, ...targetIds])])
                }
                const message = toWorkflowErrorMessage(error, copy.errors, copy.errors.retryValidation)
                if (standardTargetIds.size > 0) {
                    setChecksError(message)
                }
                if (questionTargetIds.size > 0) {
                    setQuestionsError(message)
                }
                if (!hasTableOfContents) {
                    setTableOfContentsError(toWorkflowErrorMessage(error, copy.errors, copy.errors.tableOfContentsLoadingFailed))
                }
            } finally {
                if (pipelineVersionRef.current === pipelineVersion) {
                    setIsTableOfContentsLoading(false)
                    setIsChecksRunning(false)
                    setIsQuestionsRunning(false)
                    setActiveStandardCheckProcessingIds([])
                    setActiveQuestionProcessingIds([])
                    setStandardChecks((previous) =>
                        previous.map((question) => (standardTargetIds.has(question.id) ? { ...question, isPending: false } : question))
                    )
                    setQuestions((previous) =>
                        previous.map((question) => (questionTargetIds.has(question.id) ? { ...question, isPending: false } : question))
                    )
                }
            }
        },
        [
            applyWorkflowResults,
            assertActivePipelineVersion,
            copy.errors,
            copy.standardChecks.questions,
            invokeWorkflow,
            setChecksError,
            setHasLoadedTableOfContents,
            setIsChecksRunning,
            setIsQuestionsRunning,
            setIsTableOfContentsLoading,
            setQuestions,
            setQuestionsError,
            setStandardChecks,
            setTableOfContentsEntries,
            setTableOfContentsError,
        ]
    )
    const runAnswerWorkflow = useCallback(
        async ({
            file,
            questionDefinitions,
            pipelineVersion,
        }: {
            file: File
            questionDefinitions: ValidationQuestionDefinition[]
            pipelineVersion: number
        }) => {
            const standardCheckIds = new Set(standardChecks.map((question) => question.id))
            const standardTargetIds = new Set(
                questionDefinitions.filter((question) => standardCheckIds.has(question.id)).map(({ id }) => id)
            )
            const questionTargetIds = new Set(
                questionDefinitions.filter((question) => !standardCheckIds.has(question.id)).map(({ id }) => id)
            )
            const targetIds = new Set(questionDefinitions.map(({ id }) => id))
            const receivedResultIds = new Set<string>()

            setWorkflowProgress(null)
            setWorkflowWarnings([])
            setChecksError(null)
            setQuestionsError(null)
            setFailedValidationIds((previous) => previous.filter((id) => !targetIds.has(id)))
            setIsChecksRunning(standardTargetIds.size > 0)
            setIsQuestionsRunning(questionTargetIds.size > 0)
            setActiveStandardCheckProcessingIds([...standardTargetIds])
            setActiveQuestionProcessingIds([...questionTargetIds])
            setStandardChecks((previous) =>
                previous.map((question) => (standardTargetIds.has(question.id) ? { ...question, result: null, isPending: true } : question))
            )
            setQuestions((previous) =>
                previous.map((question) => (questionTargetIds.has(question.id) ? { ...question, result: null, isPending: true } : question))
            )

            try {
                const response = await invokeWorkflow({
                    prompt: buildPlausibilityAnswerCommand(questionDefinitions),
                    files: [await buildWorkflowPdf(file)],
                    onProgress: (progress) => {
                        if (pipelineVersionRef.current !== pipelineVersion) {
                            return
                        }
                        setWorkflowProgress(progress)
                        setWorkflowWarnings(progress.warnings)
                    },
                    onPartialResult: (partialResult) => {
                        if (pipelineVersionRef.current === pipelineVersion && partialResult.step_id === 'results') {
                            const partialResults = partialResult.value as PdfValidatorWorkflowResultItem[]
                            partialResults.forEach(({ id }) => receivedResultIds.add(id))
                            applyWorkflowResults(partialResults)
                        }
                    },
                })
                assertActivePipelineVersion(pipelineVersion)

                const result = JSON.parse(response.response) as PdfValidatorAnswerWorkflowResult
                applyWorkflowResults(result.results)
                setWorkflowWarnings(result.warnings)
            } catch (error) {
                if (error instanceof WorkflowCancelledError) {
                    return
                }
                if (targetIds.size > 0 && [...targetIds].every((id) => receivedResultIds.has(id))) {
                    setFailedValidationIds((previous) => [...new Set([...previous, ...targetIds])])
                }
                const message = toWorkflowErrorMessage(error, copy.errors, copy.errors.retryValidation)
                if (standardTargetIds.size > 0) {
                    setChecksError(message)
                }
                if (questionTargetIds.size > 0) {
                    setQuestionsError(message)
                }
            } finally {
                if (pipelineVersionRef.current === pipelineVersion) {
                    setIsChecksRunning(false)
                    setIsQuestionsRunning(false)
                    setActiveStandardCheckProcessingIds([])
                    setActiveQuestionProcessingIds([])
                    setStandardChecks((previous) =>
                        previous.map((question) => (standardTargetIds.has(question.id) ? { ...question, isPending: false } : question))
                    )
                    setQuestions((previous) =>
                        previous.map((question) => (questionTargetIds.has(question.id) ? { ...question, isPending: false } : question))
                    )
                }
            }
        },
        [
            applyWorkflowResults,
            assertActivePipelineVersion,
            copy.errors,
            invokeWorkflow,
            setChecksError,
            setIsChecksRunning,
            setIsQuestionsRunning,
            setQuestions,
            setQuestionsError,
            setStandardChecks,
            standardChecks,
        ]
    )

    const cancelWorkflow = useCallback(() => {
        createNextPipelineVersion()
        setWorkflowProgress(null)
        setWorkflowWarnings([])
        setIsTableOfContentsLoading(false)
        setIsChecksRunning(false)
        setIsQuestionsRunning(false)
        setActiveStandardCheckProcessingIds([])
        setActiveQuestionProcessingIds([])
        setStandardChecks((previous) =>
            previous.map((question) => ({
                ...question,
                isPending: false,
            }))
        )
        setQuestions((previous) =>
            previous.map((question) => ({
                ...question,
                isPending: false,
            }))
        )
    }, [createNextPipelineVersion, setIsChecksRunning, setIsQuestionsRunning, setIsTableOfContentsLoading, setQuestions, setStandardChecks])
    const clearCurrentFile = useCallback(() => {
        if (isBusy || fileSelectionLockRef.current) {
            return
        }

        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl)
        }

        setPageJumpTarget(null)
        cancelWorkflow()
        setExpandedQuestionAnswers({})
        setHasStartedPlausibility(false)
        setNewQuestionValue('')
        setFailedValidationIds([])
        setUploadedFile(null)
        setPdfUrl(null)
        setPageTexts([])
        setUploadError(null)
        setChecksError(null)
        setQuestionsError(null)
        setTableOfContentsError(null)
        setTableOfContentsEntries([])
        setFocusHighlight(null)
        setHasLoadedTableOfContents(false)
        clearEditingQuestionState()
        setStandardChecks((previous) =>
            previous.map((question) => ({
                ...question,
                result: null,
                isPending: false,
            }))
        )
        setQuestions((previous) =>
            previous.map((question) => ({
                ...question,
                result: null,
                isPending: false,
            }))
        )
        setIsPreprocessing(false)
    }, [
        cancelWorkflow,
        clearEditingQuestionState,
        isBusy,
        pdfUrl,
        setChecksError,
        setFocusHighlight,
        setHasLoadedTableOfContents,
        setIsPreprocessing,
        setPageTexts,
        setPdfUrl,
        setTableOfContentsEntries,
        setTableOfContentsError,
        setQuestionsError,
        setQuestions,
        setStandardChecks,
        setUploadError,
        setUploadedFile,
    ])

    const handleFileSelection = useCallback(
        async (files: File[], { includeCurrentQuestions = false }: { includeCurrentQuestions?: boolean } = {}) => {
            if (isBusy || fileSelectionLockRef.current) {
                return
            }

            const [file] = files
            if (!file) {
                return
            }
            if (file.size > MAX_PLAYGROUND_FILE_SIZE_BYTES) {
                setUploadError(copy.upload.fileSizeErrorMessage)
                return
            }
            const rerunQuestions = includeCurrentQuestions
                ? applyPendingQuestionEdit(questions, {
                      editingQuestionId,
                      editingQuestionValue,
                  }).filter((question) => question.question.trim().length > 0)
                : []
            const questionDefinitions = [...copy.standardChecks.questions, ...rerunQuestions.map(({ id, question }) => ({ id, question }))]

            fileSelectionLockRef.current = true

            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl)
            }

            setPageJumpTarget(null)
            cancelWorkflow()
            setExpandedQuestionAnswers({})
            const pipelineVersion = pipelineVersionRef.current
            setHasStartedPlausibility(rerunQuestions.length > 0)
            setNewQuestionValue('')
            setFailedValidationIds([])
            setUploadError(null)
            setChecksError(null)
            setQuestionsError(null)
            setTableOfContentsError(null)
            setIsPreprocessing(true)
            setUploadedFile(null)
            setPdfUrl(null)
            setPageTexts([])
            setTableOfContentsEntries([])
            setFocusHighlight(null)
            setHasLoadedTableOfContents(false)
            if (includeCurrentQuestions && editingQuestionId) {
                setQuestions(rerunQuestions)
            }
            clearEditingQuestionState()
            setStandardChecks((previous) =>
                previous.map((question) => ({
                    ...question,
                    result: null,
                    isPending: false,
                }))
            )
            setQuestions((previous) =>
                previous.map((question) => ({
                    ...question,
                    result: null,
                    isPending: false,
                }))
            )

            try {
                const processed = await preprocessPdf(file)
                assertActivePipelineVersion(pipelineVersion)
                if (!processed.text) {
                    setUploadError(copy.errors.noReadableText)
                    return
                }

                setUploadedFile(file)
                setPdfUrl(URL.createObjectURL(file))
                setPageTexts(processed.pageTexts)
                setIsPreprocessing(false)
                await runAnalyzeWorkflow({ file, questionDefinitions, pipelineVersion })
            } catch (error) {
                if (!(error instanceof WorkflowCancelledError)) {
                    setUploadError(copy.errors.pdfProcessing)
                }
            } finally {
                fileSelectionLockRef.current = false
                if (pipelineVersionRef.current === pipelineVersion) {
                    setIsPreprocessing(false)
                }
            }
        },
        [
            assertActivePipelineVersion,
            cancelWorkflow,
            clearEditingQuestionState,
            copy.errors.noReadableText,
            copy.errors.pdfProcessing,
            copy.standardChecks.questions,
            copy.upload.fileSizeErrorMessage,
            editingQuestionId,
            editingQuestionValue,
            isBusy,
            pdfUrl,
            questions,
            runAnalyzeWorkflow,
            setChecksError,
            setFocusHighlight,
            setHasLoadedTableOfContents,
            setIsPreprocessing,
            setPageTexts,
            setPdfUrl,
            setQuestions,
            setQuestionsError,
            setStandardChecks,
            setTableOfContentsEntries,
            setTableOfContentsError,
            setUploadError,
            setUploadedFile,
        ]
    )
    const handleOpenUploadModal = useCallback(() => {
        if (isBusy || fileSelectionLockRef.current) {
            return
        }

        setModalSelectedFile(null)
        setIsUploadModalOpen(true)
    }, [isBusy])
    const handleCloseUploadModal = useCallback(() => {
        setModalSelectedFile(null)
        setIsUploadModalOpen(false)
    }, [])
    const handleUploadModalFileSelect = useCallback(
        (file?: File) => {
            if (!file) {
                return
            }

            setUploadError(null)
            setModalSelectedFile(file)
        },
        [setUploadError]
    )
    const handleConfirmUploadModal = useCallback(() => {
        if (!modalSelectedFile || isBusy || fileSelectionLockRef.current) {
            return
        }

        const fileToUpload = modalSelectedFile
        setModalSelectedFile(null)
        setIsUploadModalOpen(false)
        void handleFileSelection([fileToUpload])
    }, [handleFileSelection, isBusy, modalSelectedFile])
    const handleEmptyStateDrop = useCallback(
        (files?: File[]) => {
            if (isBusy || !files?.length) {
                return
            }

            if (hasFileAboveMaxSize(files)) {
                setUploadError(copy.upload.fileSizeErrorMessage)
                return
            }

            const [file] = files
            if (!file) {
                return
            }
            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                return
            }

            setUploadError(null)
            setModalSelectedFile(file)
            setIsUploadModalOpen(true)
        },
        [copy.upload.fileSizeErrorMessage, isBusy, setUploadError]
    )

    const handleUseSamplePdf = useCallback(async () => {
        if (isBusy || fileSelectionLockRef.current) {
            return
        }

        fileSelectionLockRef.current = true
        setIsPreprocessing(true)
        try {
            onSamplePdfUsed?.()
            setUploadError(null)
            const samplePdf = await loadSamplePdfFile()
            fileSelectionLockRef.current = false
            setIsPreprocessing(false)
            await handleFileSelection([samplePdf])
        } catch {
            setUploadError(copy.errors.samplePdf)
        } finally {
            fileSelectionLockRef.current = false
            setIsPreprocessing(false)
        }
    }, [copy.errors.samplePdf, handleFileSelection, isBusy, onSamplePdfUsed, setIsPreprocessing, setUploadError])
    const handleRestartAnalysis = useCallback(() => {
        if (!uploadedFile || isBusy) {
            return
        }

        void handleFileSelection([uploadedFile], { includeCurrentQuestions: true })
    }, [handleFileSelection, isBusy, uploadedFile])
    const uploadCardOptions = useMemo(
        () =>
            uploadedFile && !isBusy
                ? [
                      {
                          title: copy.upload.removeFile,
                          danger: true,
                          icon: <TrashIcon size={tokens.SizingIconS} weight="bold" />,
                          callback: () => {
                              handleCloseUploadModal()
                              clearCurrentFile()
                          },
                      },
                  ]
                : undefined,
        [clearCurrentFile, copy.upload.removeFile, handleCloseUploadModal, isBusy, uploadedFile]
    )

    const handleHighlight = useCallback(
        ({ sourceText, page }: { sourceText: string | null; page: number | null }) => {
            const source = sourceText?.trim() ?? ''
            const resolvedPage = page && Number.isFinite(page) ? Math.round(page) : null
            const preferredPageJumpTarget = !source && resolvedPage && resolvedPage < pageTexts.length ? resolvedPage + 1 : resolvedPage

            if (preferredPageJumpTarget) {
                setPageJumpTarget({ page: preferredPageJumpTarget, key: Date.now(), align: 'offset' })
            }

            if (!source && preferredPageJumpTarget) {
                setFocusHighlight({ term: '', page: preferredPageJumpTarget })
                return
            }

            if (!source) {
                setFocusHighlight(null)
                return
            }

            if (resolvedPage) {
                setFocusHighlight({ term: source, page: resolvedPage })
                return
            }

            setFocusHighlight({ term: source })
        },
        [pageTexts.length, setFocusHighlight, setPageJumpTarget]
    )

    const handleTableOfContentsEntryClick = useCallback(
        (entry: TableOfContentsEntry) => {
            const headline = entry.headline.trim()

            setFocusHighlight({
                term: headline,
                page: entry.page,
                align: 'exact',
            })
            setPageJumpTarget({ page: entry.page, key: Date.now(), align: 'exact' })
        },
        [setFocusHighlight]
    )

    const scrollPageByOffset = useCallback((offsetTop: number) => {
        if (typeof window === 'undefined') {
            return
        }

        const scrollTarget = findNearestScrollableAncestor(rootContainerRef.current)

        window.requestAnimationFrame(() => {
            if (scrollTarget instanceof Window) {
                scrollTarget.scrollBy({ top: offsetTop, behavior: 'smooth' })
                return
            }

            scrollTarget.scrollTo({
                top: scrollTarget.scrollTop + offsetTop,
                behavior: 'smooth',
            })
        })
    }, [])

    const handlePdfInitialLayoutReady = useCallback(() => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                scrollPageByOffset(100)
            })
        })
    }, [scrollPageByOffset])

    const handleRunValidationAgain = useCallback(() => {
        if (!uploadedFile || isBusy) {
            return
        }

        setHasStartedPlausibility(true)

        const nextQuestions = applyPendingQuestionEdit(questions, {
            editingQuestionId,
            editingQuestionValue,
        }).filter((question) => question.question.trim().length > 0)

        if (editingQuestionId) {
            setQuestions(nextQuestions)
            clearEditingQuestionState()
        }

        const questionDefinitions = [
            ...standardChecks
                .filter(
                    (question) =>
                        (question.result === null || failedValidationIdSet.has(question.id)) &&
                        !question.isPending &&
                        question.question.trim().length > 0
                )
                .map(({ id, question }) => ({ id, question })),
            ...nextQuestions
                .filter((question) => (question.result === null || failedValidationIdSet.has(question.id)) && !question.isPending)
                .map(({ id, question }) => ({ id, question })),
        ]

        if (questionDefinitions.length === 0) {
            setChecksError(null)
            setQuestionsError(null)
            return
        }

        scrollPageByOffset(100)
        void runAnswerWorkflow({
            file: uploadedFile,
            questionDefinitions,
            pipelineVersion: pipelineVersionRef.current,
        })
    }, [
        clearEditingQuestionState,
        editingQuestionId,
        editingQuestionValue,
        failedValidationIdSet,
        isBusy,
        questions,
        runAnswerWorkflow,
        scrollPageByOffset,
        setChecksError,
        setQuestions,
        setQuestionsError,
        standardChecks,
        uploadedFile,
    ])
    const handleAddQuestion = useCallback(() => {
        const trimmedQuestion = newQuestionValue.trim()
        if (!trimmedQuestion) {
            return
        }

        const nextQuestions = applyPendingQuestionEdit(questions, {
            editingQuestionId,
            editingQuestionValue,
        })
        const existingIds = new Set(nextQuestions.map((question) => question.id))
        const nextQuestionId = createQuestionId(existingIds)

        setQuestions([{ id: nextQuestionId, question: trimmedQuestion, result: null, isPending: false }, ...nextQuestions])
        clearEditingQuestionState()
        setNewQuestionValue('')
    }, [clearEditingQuestionState, editingQuestionId, editingQuestionValue, newQuestionValue, questions, setQuestions])

    const handleGenerateExampleQuestions = useCallback(() => {
        setQuestions((previous) => {
            const nextQuestions = applyPendingQuestionEdit(previous, {
                editingQuestionId,
                editingQuestionValue,
            })
            const existingIds = new Set(nextQuestions.map((question) => question.id))
            const existingQuestionKeys = new Set(nextQuestions.map((question) => normalizeQuestionText(question.question)))
            const exampleQuestions: ValidationQuestion[] = []

            copy.questions.examples.forEach((questionText) => {
                const normalizedQuestionText = normalizeQuestionText(questionText)
                if (existingQuestionKeys.has(normalizedQuestionText)) {
                    return
                }

                const nextQuestionId = createQuestionId(existingIds)
                existingIds.add(nextQuestionId)
                existingQuestionKeys.add(normalizedQuestionText)
                exampleQuestions.push({
                    id: nextQuestionId,
                    question: questionText,
                    result: null,
                    isPending: false,
                })
            })

            return exampleQuestions.length > 0 ? [...nextQuestions, ...exampleQuestions] : nextQuestions
        })
        clearEditingQuestionState()
        setNewQuestionValue('')
    }, [clearEditingQuestionState, copy.questions.examples, editingQuestionId, editingQuestionValue, setQuestions])

    const handleLoadPersistedQuestions = useCallback(() => {
        setQuestions(persistedQuestions.map((question) => ({ ...question, result: null, isPending: false })))
        clearEditingQuestionState()
        setNewQuestionValue('')
    }, [clearEditingQuestionState, persistedQuestions, setQuestions])

    const handleDeleteQuestion = useCallback(
        (questionId: string) => {
            deleteQuestionById(questionId)
        },
        [deleteQuestionById]
    )

    const handleStartEditingQuestion = useCallback(
        (question: ValidationQuestion) => {
            startEditingQuestion(question.id, question.question)
        },
        [startEditingQuestion]
    )

    const handleCancelEditingQuestion = useCallback(() => {
        clearEditingQuestionState()
    }, [clearEditingQuestionState])

    const handleCommitEditingQuestion = useCallback(() => {
        if (!editingQuestionId) {
            return
        }
        commitEditingQuestion()
    }, [commitEditingQuestion, editingQuestionId])

    const handleEditingQuestionKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault()
                handleCommitEditingQuestion()
            }
            if (event.key === 'Escape') {
                event.preventDefault()
                handleCancelEditingQuestion()
            }
        },
        [handleCancelEditingQuestion, handleCommitEditingQuestion]
    )
    const handleNewQuestionKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault()
                handleAddQuestion()
            }
            if (event.key === 'Escape') {
                event.preventDefault()
                setNewQuestionValue('')
            }
        },
        [handleAddQuestion]
    )

    const showContentSections = useMemo(
        () =>
            Boolean(
                uploadedFile ||
                pdfUrl ||
                pageTexts.length > 0 ||
                isPreprocessing ||
                isTableOfContentsLoading ||
                isChecksRunning ||
                isQuestionsRunning
            ),
        [isChecksRunning, isPreprocessing, isQuestionsRunning, isTableOfContentsLoading, pageTexts.length, pdfUrl, uploadedFile]
    )
    const canStartPlausibility = Boolean(uploadedFile)
    const hasQueueableStandardChecks = useMemo(
        () =>
            standardChecks.some(
                (question) =>
                    question.question.trim().length > 0 &&
                    (question.result === null || failedValidationIdSet.has(question.id)) &&
                    !question.isPending
            ),
        [failedValidationIdSet, standardChecks]
    )
    const hasQueueableQuestions = useMemo(
        () =>
            questions.some(
                (question) =>
                    question.question.trim().length > 0 &&
                    (question.result === null || failedValidationIdSet.has(question.id)) &&
                    !question.isPending
            ),
        [failedValidationIdSet, questions]
    )
    const hasQueueableValidationItems = hasQueueableStandardChecks || hasQueueableQuestions
    const hasQueuedQuestions = isQuestionsRunning
    const canRunValidationWithoutCustomQuestions =
        (hasStartedPlausibility || Boolean(checksError) || Boolean(tableOfContentsError)) && hasQueueableStandardChecks
    const activeStandardCheckProcessingIdSet = useMemo(() => new Set(activeStandardCheckProcessingIds), [activeStandardCheckProcessingIds])
    const activeQuestionProcessingIdSet = useMemo(() => new Set(activeQuestionProcessingIds), [activeQuestionProcessingIds])
    const isWorkflowPending = isTableOfContentsLoading || isChecksRunning || isQuestionsRunning
    const headerProgress = useMemo(() => {
        if (!isPreprocessing && !isWorkflowPending) {
            return null
        }

        const tableStep = workflowProgress?.steps.find((step) => step.id === 'table_of_contents')
        const resultsStep = workflowProgress?.steps.find((step) => step.id === 'results')
        const activeStep =
            workflowProgress?.steps.find((step) => step.status === 'running' || step.status === 'retrying') ?? tableStep ?? resultsStep
        const tableUnits = tableStep ? 1 : 0
        const completedTableUnits =
            tableStep && (tableStep.status === 'completed' || tableStep.status === 'not_found' || tableStep.status === 'skipped') ? 1 : 0
        const resultUnits = resultsStep?.total_units ?? 0
        const completedResultUnits = resultsStep?.completed_units ?? 0
        const totalUnits = tableUnits + resultUnits
        const completedUnits = completedTableUnits + completedResultUnits

        return {
            message: activeStep ? getLocalizedText(activeStep.description, copy.locale) : copy.progress.tableOfContents,
            value: totalUnits > 0 ? Math.min((completedUnits / totalUnits) * 100, 100) : 0,
        }
    }, [copy.locale, copy.progress.tableOfContents, isPreprocessing, isWorkflowPending, workflowProgress])
    const notesValue = watchNotes('notes') ?? ''

    useLayoutEffect(() => {
        if (typeof window === 'undefined' || !showContentSections) {
            return
        }

        const updateLeftPairHeights = () => {
            setLeftPairHeights(resolvePanelPairHeights(leftTopPanelRef.current, leftBottomPanelRef.current))
        }

        updateLeftPairHeights()

        if (typeof ResizeObserver === 'undefined') {
            return
        }

        const observer = new ResizeObserver(updateLeftPairHeights)
        if (leftTopPanelRef.current) {
            observer.observe(leftTopPanelRef.current)
        }
        if (leftBottomPanelRef.current) {
            observer.observe(leftBottomPanelRef.current)
        }

        return () => {
            observer.disconnect()
        }
    }, [
        hasLoadedTableOfContents,
        isTableOfContentsLoading,
        showContentSections,
        standardChecks,
        tableOfContentsEntries,
        tableOfContentsError,
    ])

    useLayoutEffect(() => {
        if (typeof window === 'undefined' || !showContentSections) {
            return
        }

        const updateRightPairHeights = () => {
            setRightPairHeights(resolvePanelPairHeights(rightTopPanelRef.current, rightBottomPanelRef.current, 1 / 3))
        }

        updateRightPairHeights()

        if (typeof ResizeObserver === 'undefined') {
            return
        }

        const observer = new ResizeObserver(updateRightPairHeights)
        if (rightTopPanelRef.current) {
            observer.observe(rightTopPanelRef.current)
        }
        if (rightBottomPanelRef.current) {
            observer.observe(rightBottomPanelRef.current)
        }

        return () => {
            observer.disconnect()
        }
    }, [checksError, isWorkflowPending, notesValue, questionsError, questions, showContentSections])

    const handleCopyExportNote = useCallback(() => {
        if (!notesValue) {
            return
        }

        void copyTextToClipboard(notesValue)
    }, [notesValue])
    return (
        <RootContainer
            ref={rootContainerRef}
            direction="column"
            sx={{
                maxWidth: 'none',
                margin: 0,
                gap: 0,
                padding: 0,
                paddingBottom: '20px',
                minHeight: `calc(100vh - ${TOOLBAR_HEIGHT} - 1px)`,
                backgroundColor: tokens.ColorBackgroundDark,
            }}
        >
            <PlaygroundToolHeader
                title={copy.title}
                currentLocation="/playground/pdf-validator"
                breadcrumbRoot={breadcrumbRoot}
                copy={copy.toolHeader}
                onDataPrivacyModalOpen={onDataPrivacyModalOpen}
                onInfoModalOpen={onInfoModalOpen}
                infoText={copy.infoText}
                infoModalItems={copy.infoModalItems.map((text, index) => ({
                    icon: getPdfValidatorInfoModalIcon(index),
                    text,
                }))}
                actions={
                    showContentSections ? (
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing400}>
                            {headerProgress ? (
                                <Stack
                                    direction="column"
                                    gap={tokens.SizingSpacing075}
                                    sx={{ width: '100%', maxWidth: 360, minWidth: 220, textAlign: 'right' }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={headerProgress.value}
                                            sx={{
                                                borderRadius: tokens.SizingCornerS,
                                                height: 10,
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: tokens.SizingCornerS,
                                                },
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-block',
                                                maxWidth: '100%',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                verticalAlign: 'bottom',
                                            }}
                                        >
                                            {headerProgress.message}
                                        </Box>
                                        <LoadingEllipsis />
                                    </Typography>
                                </Stack>
                            ) : null}
                            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing150}>
                                {feedbackButton ? <FeedbackTool {...feedbackButton} size="tiny" /> : null}
                                {uploadedFile && !isBusy ? (
                                    <TinyButton
                                        label={copy.upload.restartAnalysis}
                                        leftIcon={<ArrowClockwiseIcon />}
                                        onClick={handleRestartAnalysis}
                                    />
                                ) : null}
                                <TinyButton
                                    label={copy.upload.replacePdf}
                                    leftIcon={<ArrowsClockwiseIcon />}
                                    onClick={handleOpenUploadModal}
                                    disabled={isBusy}
                                />
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing150}>
                            {feedbackButton ? <FeedbackTool {...feedbackButton} size="tiny" /> : null}
                            <TextButtonNew
                                variant="primary"
                                label={copy.upload.uploadPdf}
                                leftIcon={<UploadSimpleIcon />}
                                onClick={handleOpenUploadModal}
                                disabled={isBusy}
                            />
                        </Stack>
                    )
                }
            />

            <Stack
                direction="column"
                gap={tokens.SizingSpacing200}
                sx={{
                    paddingTop: tokens.SizingSpacing200,
                    paddingRight: tokens.SizingSpacing200,
                    paddingLeft: tokens.SizingSpacing200,
                    paddingBottom: 0,
                    flex: showContentSections ? '0 0 auto' : '1 1 auto',
                    minHeight: 0,
                }}
            >
                {workflowWarnings.map((warning) => (
                    <Alert key={warning.warning_type} severity="warning">
                        {getLocalizedText(warning.message, copy.locale)}
                    </Alert>
                ))}
                <Modal
                    fullWidth
                    width="600px"
                    showCloseIcon
                    closeIconTooltip={copy.toolHeader.close}
                    isOpen={isUploadModalOpen}
                    onClose={handleCloseUploadModal}
                    title={uploadedFile ? copy.upload.replacePdf : copy.upload.uploadValuationPdf}
                    showLoading={isPreprocessing}
                    footerProps={{
                        primaryButton: {
                            text: uploadedFile ? copy.upload.replacePdf : copy.upload.uploadPdf,
                            icon: uploadedFile ? <ArrowsClockwiseIcon /> : <UploadSimpleIcon />,
                            disabled: !modalSelectedFile,
                            onClick: handleConfirmUploadModal,
                        },
                        secondaryButton: {
                            text: copy.upload.cancel,
                            onClick: handleCloseUploadModal,
                        },
                    }}
                >
                    <Stack width="100%">
                        <FileUploadCard
                            title={copy.upload.valuationPdf}
                            description={copy.upload.dropDescription}
                            file={modalSelectedFile ?? uploadedFile ?? undefined}
                            setFile={handleUploadModalFileSelect}
                            allowedFileTypes={['application/pdf']}
                            disabled={false}
                            errorMessage={uploadError ?? undefined}
                            options={uploadCardOptions}
                            maxFileSizeBytes={MAX_PLAYGROUND_FILE_SIZE_BYTES}
                            fileSizeErrorMessage={copy.upload.fileSizeErrorMessage}
                        />
                    </Stack>
                </Modal>

                {showContentSections ? (
                    <PageLayoutGrid>
                        <PanelColumn direction="column" sx={{ maxHeight: PANEL_COLUMN_MAX_HEIGHT }}>
                            <PanelCard
                                ref={leftTopPanelRef}
                                direction="column"
                                gap={tokens.SizingSpacing100}
                                sx={{
                                    minHeight: '128px',
                                    maxHeight: leftPairHeights.top ? `${leftPairHeights.top}px` : undefined,
                                    overflowY: 'auto',
                                }}
                            >
                                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing050}>
                                    <Headline variant="headline3" title={copy.standardChecks.title} fullWidth={false} />
                                    <InfoTooltip text={copy.standardChecks.tooltip} />
                                </Stack>
                                {checksError ? <Alert severity="error">{checksError}</Alert> : null}
                                <Stack direction="column" gap="0" width="100%">
                                    {standardChecks.map((question) => {
                                        const result = question.result
                                        const canHighlight = Boolean(result?.sourceText?.trim() || result?.page)
                                        const tooltip = formatStandardCheckTooltip(result, copy)
                                        const isActivePendingStandardCheck = activeStandardCheckProcessingIdSet.has(question.id)

                                        const row = (
                                            <Box
                                                key={question.id}
                                                onClick={() => {
                                                    if (canHighlight) {
                                                        handleHighlight({
                                                            sourceText: result?.sourceText ?? null,
                                                            page: result?.page ?? null,
                                                        })
                                                    }
                                                }}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: tokens.SizingSpacing100,
                                                    padding: `${tokens.SizingSpacing075} ${tokens.SizingSpacing100}`,
                                                    borderRadius: tokens.SizingCornerS,
                                                    cursor: canHighlight ? 'pointer' : 'default',
                                                    backgroundColor: tokens.ColorSurfaceDefault,
                                                    '&:hover': canHighlight
                                                        ? {
                                                              backgroundColor: tokens.ColorSurfaceHover,
                                                              '& .standard-check-hover-arrow': {
                                                                  opacity: 1,
                                                              },
                                                          }
                                                        : undefined,
                                                }}
                                            >
                                                <Box sx={{ display: 'inline-flex', flexShrink: 0 }}>
                                                    {renderStandardCheckIcon({
                                                        result: question.result,
                                                        isPending: question.isPending,
                                                        isActivePending: isActivePendingStandardCheck,
                                                    })}
                                                </Box>
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="textS">
                                                        {question.question}
                                                        {isActivePendingStandardCheck ? <LoadingEllipsis /> : null}
                                                    </Typography>
                                                </Box>
                                                {canHighlight ? (
                                                    <Box
                                                        className="standard-check-hover-arrow"
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                            opacity: 0,
                                                            transition: 'opacity 120ms ease',
                                                            color: tokens.ColorIconDefault,
                                                        }}
                                                    >
                                                        <ArrowRightIcon size={tokens.SizingIconS} weight="bold" />
                                                    </Box>
                                                ) : null}
                                            </Box>
                                        )

                                        return tooltip ? (
                                            <Tooltip key={question.id} title={tooltip}>
                                                {row}
                                            </Tooltip>
                                        ) : (
                                            row
                                        )
                                    })}
                                </Stack>
                            </PanelCard>
                            <PanelCard
                                ref={leftBottomPanelRef}
                                direction="column"
                                gap={tokens.SizingSpacing100}
                                sx={{
                                    minHeight: '96px',
                                    maxHeight: leftPairHeights.bottom ? `${leftPairHeights.bottom}px` : undefined,
                                    overflowY: 'auto',
                                }}
                            >
                                <Headline variant="headline3" title={copy.tableOfContents.title} />
                                {isTableOfContentsLoading ? (
                                    <TableOfContentsLoadingState copy={copy} />
                                ) : tableOfContentsError ? (
                                    <Alert severity="error">{tableOfContentsError}</Alert>
                                ) : tableOfContentsEntries.length > 0 ? (
                                    <Stack direction="column" gap="0">
                                        {tableOfContentsEntries.map((entry) => (
                                            <Box
                                                key={`${entry.level}-${entry.page}-${entry.headline}`}
                                                onClick={() => handleTableOfContentsEntryClick(entry)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'space-between',
                                                    gap: tokens.SizingSpacing100,
                                                    padding: `${tokens.SizingSpacing075} ${tokens.SizingSpacing100}`,
                                                    paddingLeft:
                                                        entry.level === 2
                                                            ? `calc(${tokens.SizingSpacing100} + ${tokens.SizingSpacing150})`
                                                            : tokens.SizingSpacing100,
                                                    borderRadius: tokens.SizingCornerS,
                                                    cursor: 'pointer',
                                                    transition: 'background-color 120ms ease',
                                                    '&:hover': {
                                                        backgroundColor: tokens.ColorSurfaceHover,
                                                    },
                                                }}
                                            >
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        variant="textS"
                                                        color={entry.level === 2 ? tokens.ColorTextDefault : tokens.ColorTextBold}
                                                    >
                                                        {entry.headline}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                                    {entry.page}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : hasLoadedTableOfContents ? (
                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                        {copy.tableOfContents.empty}
                                    </Typography>
                                ) : (
                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                        {copy.tableOfContents.pending}
                                    </Typography>
                                )}
                            </PanelCard>
                        </PanelColumn>

                        <PdfStage>
                            <PdfPreviewSection
                                pdfUrl={pdfUrl}
                                pageTexts={pageTexts}
                                focusHighlight={focusHighlight}
                                onFocusHighlightChange={setFocusHighlight}
                                floatingSearchControls
                                pageJumpTarget={pageJumpTarget}
                                showPageShadow
                                onInitialLayoutReady={handlePdfInitialLayoutReady}
                                initialDocumentMinHeightPx={PDF_STAGE_HEIGHT_PX}
                                copy={copy.pdfPreview}
                            />
                        </PdfStage>

                        <PanelColumn
                            direction="column"
                            sx={{
                                maxHeight: PANEL_COLUMN_MAX_HEIGHT,
                            }}
                        >
                            <PanelCard
                                ref={rightTopPanelRef}
                                direction="column"
                                gap={tokens.SizingSpacing100}
                                sx={{
                                    maxHeight: rightPairHeights.top ? `${rightPairHeights.top}px` : undefined,
                                    overflow: 'hidden',
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={tokens.SizingSpacing100}>
                                    <Headline variant="headline3" title={copy.notes.title} fullWidth={false} />
                                    <Tooltip title={copy.notes.copy}>
                                        <Box component="span">
                                            <IconButton
                                                size="small"
                                                variant="tertiary"
                                                icon={<CopyIcon />}
                                                onClick={handleCopyExportNote}
                                                disabled={!notesValue}
                                                ariaLabel={copy.notes.copy}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Stack>
                                <FormMarkdownField
                                    control={notesControl}
                                    fieldName="notes"
                                    defaultValue=""
                                    errors={notesErrors}
                                    label=""
                                    hideLabel
                                    placeholder={copy.notes.placeholder}
                                    fullWidth
                                    xs={12}
                                    inputFieldStyles={{
                                        minHeight: '100px',
                                        maxHeight: rightPairHeights.top
                                            ? `${Math.max(rightPairHeights.top - NOTES_EDITOR_CHROME_HEIGHT_PX, 120)}px`
                                            : undefined,
                                    }}
                                    enableHeadings
                                    copy={copy.notes.textEditor}
                                />
                            </PanelCard>

                            <QuestionsPanel
                                ref={rightBottomPanelRef}
                                direction="column"
                                gap={tokens.SizingSpacing150}
                                sx={{
                                    minHeight: 0,
                                    maxHeight: rightPairHeights.bottom ? `${rightPairHeights.bottom}px` : undefined,
                                }}
                            >
                                <Stack direction="column" gap={tokens.SizingSpacing150} sx={{ minHeight: 0, position: 'relative' }}>
                                    <Stack
                                        minHeight="38px"
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap={tokens.SizingSpacing150}
                                    >
                                        <Headline variant="headline3" title={copy.questions.title} fullWidth={false} />
                                        <TextButtonNew
                                            variant="primary"
                                            label={hasQueuedQuestions ? copy.questions.queued : copy.questions.start}
                                            leftIcon={<SparkleIcon />}
                                            onClick={handleRunValidationAgain}
                                            disabled={
                                                (!hasAnyQuestions && !canRunValidationWithoutCustomQuestions) ||
                                                !canStartPlausibility ||
                                                !hasQueueableValidationItems ||
                                                isBusy ||
                                                hasQueuedQuestions
                                            }
                                        />
                                    </Stack>

                                    {questionsError ? <Alert severity="error">{questionsError}</Alert> : null}
                                    {hasStartedPlausibility && hasQueueableQuestions ? (
                                        <InfoBox variant="ordinary" size="small">
                                            <Typography variant="textS" color={tokens.ColorTextBold}>
                                                {copy.questions.startInfo}
                                            </Typography>
                                        </InfoBox>
                                    ) : null}

                                    <Stack direction="column" sx={{ minHeight: 0 }}>
                                        <Stack
                                            direction="column"
                                            gap={tokens.SizingSpacing100}
                                            sx={{
                                                minHeight: 0,
                                                paddingRight: tokens.SizingSpacing050,
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing150}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <TextField
                                                        fullWidth
                                                        placeholder={copy.questions.newPlaceholder}
                                                        value={newQuestionValue}
                                                        onChange={(event) => setNewQuestionValue(event.target.value)}
                                                        onKeyDown={handleNewQuestionKeyDown}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                minHeight: '46px',
                                                                backgroundColor: tokens.ColorSurfaceDefault,
                                                                borderRadius: tokens.SizingCornerS,
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                padding: `0 ${tokens.SizingSpacing150}`,
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                                <IconButton
                                                    size="medium"
                                                    variant="tertiary"
                                                    icon={<CheckIcon />}
                                                    onClick={handleAddQuestion}
                                                    disabled={newQuestionValue.trim().length === 0}
                                                    ariaLabel={copy.questions.create}
                                                    tooltip={copy.questions.create}
                                                />
                                            </Stack>

                                            {!hasAnyQuestions ? (
                                                <Stack
                                                    direction="column"
                                                    alignItems="center"
                                                    gap={tokens.SizingSpacing150}
                                                    sx={{
                                                        padding: tokens.SizingSpacing200,
                                                        borderRadius: tokens.SizingCornerM,
                                                    }}
                                                >
                                                    <EmptyThreadsImage height="196px" width="196px" />
                                                    <Box sx={{ width: '100%', maxWidth: '460px', textAlign: 'center' }}>
                                                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                                                            {hasPersistedQuestions
                                                                ? copy.questions.emptyWithPersisted
                                                                : copy.questions.emptyWithoutPersisted}
                                                        </Typography>
                                                    </Box>
                                                    <Stack
                                                        direction="row"
                                                        flexWrap="wrap"
                                                        justifyContent="center"
                                                        gap={tokens.SizingSpacing150}
                                                    >
                                                        <TinyButton
                                                            onClick={handleGenerateExampleQuestions}
                                                            leftIcon={<SparkleIcon />}
                                                            label={copy.questions.generateExamples}
                                                        />
                                                        {hasPersistedQuestions ? (
                                                            <TinyButton
                                                                onClick={handleLoadPersistedQuestions}
                                                                leftIcon={<ArrowClockwiseIcon />}
                                                                label={copy.questions.loadPast}
                                                            />
                                                        ) : null}
                                                    </Stack>
                                                </Stack>
                                            ) : null}

                                            {questions.map((question) => {
                                                const result = question.result
                                                const canHighlight = Boolean(result?.sourceText?.trim() || result?.page)
                                                const isEditingQuestion = editingQuestionId === question.id
                                                const isActivePendingQuestion = activeQuestionProcessingIdSet.has(question.id)
                                                const shouldBlurQuestionCard =
                                                    !isEditingQuestion && result === null && !isActivePendingQuestion
                                                const shouldShowStatusIcon = question.isPending || result !== null
                                                const answerPreviewText = getValidationAnswerPreviewText(result, copy)
                                                const showExpandableAnswer = Boolean(answerPreviewText)
                                                const answerSurfaceColor = getExpandableAnswerSurfaceColor(result?.status ?? null)
                                                const expansionKey = `question:${question.id}`
                                                const isAnswerExpanded = Boolean(expandedQuestionAnswers[expansionKey])
                                                const isCardHovered = hoveredQuestionCardId === question.id
                                                const isAnswerHovered = hoveredQuestionAnswerId === question.id

                                                return (
                                                    <Stack
                                                        key={question.id}
                                                        direction="column"
                                                        gap={tokens.SizingSpacing100}
                                                        onMouseEnter={() => setHoveredQuestionCardId(question.id)}
                                                        onMouseLeave={() => {
                                                            setHoveredQuestionCardId((previous) =>
                                                                previous === question.id ? null : previous
                                                            )
                                                            setHoveredQuestionAnswerId((previous) =>
                                                                previous === question.id ? null : previous
                                                            )
                                                        }}
                                                        onClick={() => {
                                                            if (canHighlight) {
                                                                handleHighlight({
                                                                    sourceText: result?.sourceText ?? null,
                                                                    page: result?.page ?? null,
                                                                })
                                                            }
                                                        }}
                                                        sx={{
                                                            border: primaryBorder,
                                                            borderRadius: tokens.SizingCornerM,
                                                            padding: tokens.SizingSpacing100,
                                                            backgroundColor:
                                                                canHighlight && isCardHovered && !isAnswerHovered
                                                                    ? tokens.ColorSurfaceHover
                                                                    : tokens.ColorSurfaceDefault,
                                                            cursor: canHighlight ? 'pointer' : 'default',
                                                            transition: 'background-color 120ms ease',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                filter: shouldBlurQuestionCard ? 'blur(0.35px)' : 'none',
                                                                transition: 'filter 120ms ease',
                                                            }}
                                                        >
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                gap={tokens.SizingSpacing100}
                                                                sx={{
                                                                    borderRadius: tokens.SizingCornerS,
                                                                }}
                                                            >
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    {isEditingQuestion ? (
                                                                        <Box onClick={(event) => event.stopPropagation()}>
                                                                            <TextField
                                                                                size="small"
                                                                                fullWidth
                                                                                value={editingQuestionValue}
                                                                                onChange={(event) =>
                                                                                    updateEditingQuestionValue(event.target.value)
                                                                                }
                                                                                onBlur={handleCommitEditingQuestion}
                                                                                onKeyDown={handleEditingQuestionKeyDown}
                                                                                autoFocus
                                                                            />
                                                                        </Box>
                                                                    ) : (
                                                                        <Typography variant="textS">{question.question}</Typography>
                                                                    )}
                                                                </Box>
                                                                {isEditingQuestion ? (
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        gap={tokens.SizingSpacing050}
                                                                        flexShrink={0}
                                                                    >
                                                                        <Box
                                                                            onMouseDown={(event) => event.preventDefault()}
                                                                            onClick={(event) => event.stopPropagation()}
                                                                        >
                                                                            <IconButton
                                                                                size="small"
                                                                                variant="tertiary"
                                                                                icon={<CheckIcon />}
                                                                                onClick={handleCommitEditingQuestion}
                                                                                ariaLabel={copy.questions.save}
                                                                                tooltip={copy.questions.save}
                                                                            />
                                                                        </Box>
                                                                        <Box
                                                                            onMouseDown={(event) => event.preventDefault()}
                                                                            onClick={(event) => event.stopPropagation()}
                                                                        >
                                                                            <IconButton
                                                                                size="small"
                                                                                variant="tertiary"
                                                                                icon={<XIcon />}
                                                                                onClick={handleCancelEditingQuestion}
                                                                                ariaLabel={copy.questions.cancelEdit}
                                                                                tooltip={copy.questions.cancelEdit}
                                                                            />
                                                                        </Box>
                                                                    </Stack>
                                                                ) : shouldShowStatusIcon ? (
                                                                    <Box sx={{ display: 'inline-flex', flexShrink: 0 }}>
                                                                        {renderValidationQuestionStatusIcon({
                                                                            result,
                                                                            isPending: question.isPending,
                                                                            isActivePending: isActivePendingQuestion,
                                                                        })}
                                                                    </Box>
                                                                ) : (
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        gap={tokens.SizingSpacing050}
                                                                        flexShrink={0}
                                                                    >
                                                                        <Box onClick={(event) => event.stopPropagation()}>
                                                                            <IconButton
                                                                                size="small"
                                                                                variant="tertiary"
                                                                                icon={<PencilSimpleIcon />}
                                                                                onClick={() => {
                                                                                    setTimeout(() => {
                                                                                        handleStartEditingQuestion(question)
                                                                                    }, 0)
                                                                                }}
                                                                                ariaLabel={copy.questions.edit}
                                                                                tooltip={copy.questions.edit}
                                                                            />
                                                                        </Box>
                                                                        <Box onClick={(event) => event.stopPropagation()}>
                                                                            <IconButton
                                                                                size="small"
                                                                                variant="tertiary"
                                                                                icon={<TrashIcon />}
                                                                                onClick={() => handleDeleteQuestion(question.id)}
                                                                                ariaLabel={copy.questions.delete}
                                                                                tooltip={copy.questions.delete}
                                                                            />
                                                                        </Box>
                                                                    </Stack>
                                                                )}
                                                            </Stack>

                                                            {showExpandableAnswer ? (
                                                                <Box
                                                                    onMouseEnter={() => setHoveredQuestionAnswerId(question.id)}
                                                                    onMouseLeave={() =>
                                                                        setHoveredQuestionAnswerId((previous) =>
                                                                            previous === question.id ? null : previous
                                                                        )
                                                                    }
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        toggleExpandedQuestionAnswer(expansionKey)
                                                                    }}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: isAnswerExpanded ? 'flex-start' : 'center',
                                                                        gap: tokens.SizingSpacing050,
                                                                        marginTop: tokens.SizingSpacing050,
                                                                        padding: tokens.SizingSpacing050,
                                                                        borderRadius: tokens.SizingCornerS,
                                                                        backgroundColor: answerSurfaceColor,
                                                                        cursor: 'pointer',
                                                                    }}
                                                                >
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        {isAnswerExpanded && result?.answer ? (
                                                                            result.status === 'text_answer' ? (
                                                                                <StyledMarkdownAnswer>
                                                                                    <Markdown remarkPlugins={[remarkGfm]}>
                                                                                        {result.answer}
                                                                                    </Markdown>
                                                                                </StyledMarkdownAnswer>
                                                                            ) : (
                                                                                <Typography
                                                                                    variant="textXS"
                                                                                    color={getValidationAnswerTextColor(
                                                                                        result?.status ?? null
                                                                                    )}
                                                                                >
                                                                                    {answerPreviewText}
                                                                                </Typography>
                                                                            )
                                                                        ) : (
                                                                            <Typography
                                                                                variant="textXS"
                                                                                color={getValidationAnswerTextColor(result?.status ?? null)}
                                                                                noWrap
                                                                            >
                                                                                {answerPreviewText}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            flexShrink: 0,
                                                                            color: tokens.ColorIconBoldest,
                                                                        }}
                                                                    >
                                                                        {isAnswerExpanded ? (
                                                                            <CaretUpIcon size={tokens.SizingIconS} weight="bold" />
                                                                        ) : (
                                                                            <CaretDownIcon size={tokens.SizingIconS} weight="bold" />
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            ) : null}
                                                        </Box>
                                                    </Stack>
                                                )
                                            })}
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    flex: '0 0 auto',
                                                    height: tokens.SizingSpacing100,
                                                    minHeight: tokens.SizingSpacing100,
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </QuestionsPanel>
                        </PanelColumn>
                    </PageLayoutGrid>
                ) : (
                    <Stack direction="column" flex={1} justifyContent="center" alignItems="center">
                        <FileDropArea
                            allowedFileTypes={['application/pdf']}
                            maxFileSizeBytes={MAX_PLAYGROUND_FILE_SIZE_BYTES}
                            fileSizeErrorMessage={copy.upload.fileSizeErrorMessage}
                            onFileValidationError={setUploadError}
                            setIsOver={setIsEmptyStateDropOver}
                            onDataDrop={(_areaId?: string, files?: File[]) => {
                                handleEmptyStateDrop(files)
                            }}
                            width="532px"
                        >
                            <EmptyPdfStateCard
                                direction="column"
                                justifyContent="center"
                                sx={{
                                    backgroundColor: isEmptyStateDropOver ? tokens.ColorSurfaceHover : tokens.ColorSurfaceDefault,
                                }}
                            >
                                <EmptyPdfStateContent direction="column">
                                    <EmptyPdfStateIllustration height="270px" width="270px" />
                                    <Typography variant="headline2" color={tokens.ColorTextBoldest}>
                                        {copy.upload.emptyTitle}
                                    </Typography>
                                    <Typography variant="textM" color={tokens.ColorTextDefault}>
                                        {copy.emptyDescription}
                                    </Typography>
                                    <Stack direction="row" gap={tokens.SizingSpacing150}>
                                        <TinyButton
                                            onClick={handleOpenUploadModal}
                                            leftIcon={<UploadSimpleIcon />}
                                            label={copy.upload.uploadPdf}
                                            tooltip={copy.upload.uploadTooltip}
                                        />
                                        <TinyButton
                                            onClick={handleUseSamplePdf}
                                            leftIcon={<FastForwardIcon />}
                                            label={copy.upload.sampleButton}
                                            disabled={isBusy}
                                            tooltip={copy.upload.sampleTooltip}
                                        />
                                    </Stack>
                                </EmptyPdfStateContent>
                            </EmptyPdfStateCard>
                        </FileDropArea>
                        {uploadError === copy.upload.fileSizeErrorMessage ? (
                            <Box sx={{ marginTop: tokens.SizingSpacing150 }}>
                                <InfoBox variant="negative" size="small" icon={<WarningIcon />}>
                                    {uploadError}
                                </InfoBox>
                            </Box>
                        ) : uploadError ? (
                            <Alert severity="error" sx={{ marginTop: tokens.SizingSpacing150 }}>
                                {uploadError}
                            </Alert>
                        ) : null}
                    </Stack>
                )}
            </Stack>
        </RootContainer>
    )
}
